// Client portal UI dictionary. Separate from the CRM translations because the
// portal has its own auth and its own audience (the client, not the agent).
// Agent-authored content (timeline comments, next step, blocker text, chat
// messages) is NOT translated — it is shown as written.

export const portalTranslations = {
  en: {
    common: {
      signOut: 'Sign out',
      loading: 'Loading...',
      upload: 'Upload',
      uploading: 'Uploading...',
      preview: 'Preview',
      view: 'View',
      download: 'Download',
      replace: 'Replace',
      remove: 'Remove',
      send: 'Send',
      now: 'Now',
      done: 'Done',
      clientPortal: 'Client portal',
      backToOverview: 'Back to overview',
    },
    header: {
      title: 'Legal Client Portal',
      subtitle: 'Confidential matter workspace',
      workspaceBadge: 'Attorney-client workspace',
    },
    tabs: {
      dashboard: 'Overview',
      chat: 'Messages',
      documents: 'Documents',
      timeline: 'Case Progress',
      settings: 'Security',
    },
    sidebar: {
      activeCase: 'Active case',
      caseSubtitle: 'Status, required actions, documents, and messages in one workspace.',
      caseReadiness: 'Case readiness',
      uploadDocuments: 'Upload documents',
      messageLegalTeam: 'Message legal team',
      needHelp: 'Need help?',
      needHelpBody: 'Use Messages. Your lawyer sees the full case context.',
    },
    blocker: {
      onHold: 'Case on hold',
      waitingClient: 'Action needed from you',
      waitingUs: 'Case on hold — our team is on it',
      waitingThirdParty: 'Case on hold — waiting on a third party',
      estimated: 'Estimated: {eta}',
      messageLegalTeam: 'Message legal team',
    },
    dashboard: {
      caseOverview: 'Case overview',
      hello: 'Hello, {name}',
      underReview: '{title} is currently under legal review.',
      overviewIntro: 'The most important items are separated below: what needs your attention, where the case stands, new documents, and messages from the legal team.',
      nextAction: 'Next action',
      readiness: 'Readiness',
      readinessDetail: 'Case file completeness',
      documents: 'Documents',
      verifiedCount: '{count} verified',
      messages: 'Messages',
      latestAt: 'Latest {date}',
      noMessagesYet: 'No messages yet',
      locatedFunds: 'Located funds',
      locatedFundsDetail: 'Visible case assets',
      caseTimeline: 'Case timeline',
      noCaseEventsYet: 'No case events yet',
      timelineNote: 'Events shown here are published from the CRM case timeline.',
      noVisibleTimeline: 'No CRM timeline events are visible in the client portal yet.',
      caseUpdate: 'Case update',
      timelineEventBody: 'CRM timeline event',
      fallbackReplaceDeclined: 'Replace declined verification documents',
      fallbackUploadMissing: 'Upload missing verification documents',
      fallbackWaitReview: 'Wait for legal team review',
      fallbackReviewUpdate: 'Review the latest matter update',
      caseActivity: 'Case activity',
      whatChanged: 'What changed recently',
      viewProgress: 'View progress',
      loadingActivity: 'Loading case activity...',
      noPublishedUpdates: 'Your legal team has not published timeline updates yet.',
      newTimelineUpdate: 'A new update was added to your case timeline.',
      viewAll: 'View all',
      noDocsYet: 'No documents uploaded yet.',
      openChat: 'Open chat',
      you: 'You',
      legalTeam: 'Legal team',
      details: 'Details',
      legalMatter: 'Legal Matter',
      inProgress: 'In progress',
      assignedTeam: 'Assigned legal team',
    },
    progressStages: {
      none: 'Case opened',
      documents_confirmed: 'Documents confirmed',
      funds_located: 'Funds located',
      payout_in_progress: 'Payout in progress',
      completed: 'Funds received — case closed',
    },
    funds: {
      emptyState: 'When your legal team marks located funds as visible, they will appear here.',
      cryptoLocated: 'Cryptocurrency located',
      bankLocated: 'Bank funds located',
      source: 'Source:',
      account: 'Account:',
      viewBlockchain: 'View blockchain record',
    },
    timeline: {
      locatedFunds: 'Located funds',
      fundsTitle: 'Funds identified by your legal team',
      fundsNote: 'These items appear only when the CRM record is marked as visible for the client portal.',
      progressLabel: 'Legal matter progress',
      updatesTitle: 'Matter Updates',
      updatesNote: 'Key actions, review milestones, and published notes.',
      publishedBy: 'Published by your legal team',
      loadingUpdates: 'Loading updates...',
      emptyTitle: 'No matter updates yet',
      emptyBody: 'Your legal team will publish confirmed milestones here as soon as they are ready.',
    },
    chat: {
      confidential: 'Confidential communication',
      title: 'Secure Messages',
      subtitle: 'Ask questions, clarify documents, and attach files for your case team.',
      emptyTitle: 'No messages yet',
      emptyBody: 'Write to your legal team when you need clarification, an update, or help with a document.',
      attachFile: 'Attach file',
      placeholder: 'Write to your legal team...',
      sendFailed: 'Message was not sent. Please try again.',
    },
    documents: {
      centerLabel: 'Case document center',
      title: 'Documents & Verification',
      subtitle: 'Upload identity documents, share case files, and download materials your legal team publishes.',
      verifiedBadge: '{count} verified',
      verification: 'Verification',
      caseFiles: 'Case Files',
      files: 'Files',
      invoices: 'Invoices',
      identityTitle: 'Identity verification for your legal file',
      identityNote: 'Upload clear, current documents. Your legal team will mark each item as approved, declined, or pending review.',
      notUploadedYet: 'Not uploaded yet',
      awaitingUpload: 'Awaiting upload',
      approvedNote: 'Approved for your legal file',
      declinedNote: 'Declined. Please upload a clearer version for review.',
      pendingNote: 'Pending legal team review',
      uploadToContinue: 'Upload this document to continue verification',
      caseTeamFilesTitle: 'Files for your case team',
      caseTeamFilesNote: 'Contracts, correspondence, evidence, statements, and other supporting materials.',
      uploadFile: 'Upload File',
      emptyTitle: 'No documents yet',
      emptyBody: 'When a file is ready, it will appear here with its review status.',
      loadingPreview: 'Loading preview...',
      previewUnavailable: 'Preview unavailable',
      previewUnavailableNote: 'You can still download this document.',
      previewUnsupported: 'Preview is not available for this file type',
      previewUnsupportedNote: 'Download the file to open it locally.',
      closePreview: 'Close preview',
      uploadFailed: 'Upload failed. Please try again.',
    },
    verifyTypes: {
      'Passport': 'Passport',
      'Driver License': 'Driver License',
      'ID Front': 'ID Front',
      'ID Back': 'ID Back',
      'Utility Bill': 'Utility Bill',
      'Bank Statement': 'Bank Statement',
    },
    status: {
      approved: 'Approved',
      declined: 'Declined',
      pending: 'Pending',
      missing: 'Missing',
      uploaded: 'Uploaded',
      active: 'Active',
      new: 'New',
      inProgress: 'In progress',
      opened: 'Opened',
      onHold: 'On hold',
      closed: 'Closed',
    },
    settings: {
      protection: 'Portal protection',
      title: 'Account Security',
      subtitle: 'Keep access to your confidential legal matter protected.',
      currentPassword: 'Current Password',
      currentPlaceholder: 'Enter current password',
      newPassword: 'New Password',
      newPlaceholder: 'Minimum 6 characters',
      confirmPassword: 'Confirm New Password',
      confirmPlaceholder: 'Repeat new password',
      changed: 'Password changed successfully.',
      saving: 'Saving...',
      update: 'Update Password',
      guidanceTitle: 'Security guidance',
      guidanceBody: 'Use a unique password and sign out after working on shared devices. Your portal is separated from the internal legal workspace.',
      passwordsDontMatch: 'New passwords do not match.',
      changeFailed: 'Failed to change password.',
    },
    login: {
      title: 'Client Portal',
      subtitle: 'Sign in to view your case information',
      loginLabel: 'Login (Email)',
      passwordLabel: 'Password',
      signIn: 'Sign In',
      signingIn: 'Signing in...',
      invalidCredentials: 'Invalid login or password. Please try again.',
      autoLoggingIn: 'Opening portal…',
      autoLoginFailed: 'Link expired or invalid. Please use login/password.',
      invalidLink: 'Invalid link.',
      gotoLogin: 'Go to login page',
      needHelp: 'Need help? Contact your lawyer directly.',
    },
  },
  ru: {
    common: {
      signOut: 'Выйти',
      loading: 'Загрузка...',
      upload: 'Загрузить',
      uploading: 'Загрузка...',
      preview: 'Просмотр',
      view: 'Открыть',
      download: 'Скачать',
      replace: 'Заменить',
      remove: 'Убрать',
      send: 'Отправить',
      now: 'Сейчас',
      done: 'Готово',
      clientPortal: 'Кабинет клиента',
      backToOverview: 'К обзору',
    },
    header: {
      title: 'Кабинет клиента',
      subtitle: 'Конфиденциальный кабинет по вашему делу',
      workspaceBadge: 'Защищённое пространство',
    },
    tabs: {
      dashboard: 'Обзор',
      chat: 'Сообщения',
      documents: 'Документы',
      timeline: 'Ход дела',
      settings: 'Безопасность',
    },
    sidebar: {
      activeCase: 'Активное дело',
      caseSubtitle: 'Статус, необходимые действия, документы и переписка — в одном месте.',
      caseReadiness: 'Готовность дела',
      uploadDocuments: 'Загрузить документы',
      messageLegalTeam: 'Написать юристам',
      needHelp: 'Нужна помощь?',
      needHelpBody: 'Напишите в «Сообщения». Ваш юрист видит весь контекст дела.',
    },
    blocker: {
      onHold: 'Дело приостановлено',
      waitingClient: 'Требуется действие с вашей стороны',
      waitingUs: 'Дело приостановлено — наша команда работает над этим',
      waitingThirdParty: 'Дело приостановлено — ждём ответ третьей стороны',
      estimated: 'Ориентировочно: {eta}',
      messageLegalTeam: 'Написать юристам',
    },
    dashboard: {
      caseOverview: 'Обзор дела',
      hello: 'Здравствуйте, {name}',
      underReview: '{title} — сейчас на юридической проверке.',
      overviewIntro: 'Самое важное собрано ниже: что требует вашего внимания, на каком этапе дело, новые документы и сообщения от юристов.',
      nextAction: 'Следующий шаг',
      readiness: 'Готовность',
      readinessDetail: 'Полнота материалов дела',
      documents: 'Документы',
      verifiedCount: 'Подтверждено: {count}',
      messages: 'Сообщения',
      latestAt: 'Последнее {date}',
      noMessagesYet: 'Сообщений пока нет',
      locatedFunds: 'Найденные средства',
      locatedFundsDetail: 'Видимые активы по делу',
      caseTimeline: 'Хронология дела',
      noCaseEventsYet: 'Событий по делу пока нет',
      timelineNote: 'События публикуются юристами из хронологии дела.',
      noVisibleTimeline: 'В кабинете пока нет видимых событий по делу.',
      caseUpdate: 'Обновление по делу',
      timelineEventBody: 'Событие хронологии дела',
      fallbackReplaceDeclined: 'Замените отклонённые документы верификации',
      fallbackUploadMissing: 'Загрузите недостающие документы верификации',
      fallbackWaitReview: 'Дождитесь проверки юристами',
      fallbackReviewUpdate: 'Посмотрите последнее обновление по делу',
      caseActivity: 'Активность по делу',
      whatChanged: 'Что изменилось недавно',
      viewProgress: 'Смотреть ход дела',
      loadingActivity: 'Загружаем активность по делу...',
      noPublishedUpdates: 'Юристы ещё не опубликовали обновления по делу.',
      newTimelineUpdate: 'В хронологию дела добавлено новое обновление.',
      viewAll: 'Все документы',
      noDocsYet: 'Документы ещё не загружены.',
      openChat: 'Открыть чат',
      you: 'Вы',
      legalTeam: 'Юристы',
      details: 'Подробнее',
      legalMatter: 'Ваше дело',
      inProgress: 'В работе',
      assignedTeam: 'Назначенная команда юристов',
    },
    progressStages: {
      none: 'Дело открыто',
      documents_confirmed: 'Документы подтверждены',
      funds_located: 'Средства найдены',
      payout_in_progress: 'Выплата выполняется',
      completed: 'Средства получены — дело закрыто',
    },
    funds: {
      emptyState: 'Когда юристы отметят найденные средства как видимые, они появятся здесь.',
      cryptoLocated: 'Найдена криптовалюта',
      bankLocated: 'Найдены банковские средства',
      source: 'Источник:',
      account: 'Счёт:',
      viewBlockchain: 'Смотреть запись в блокчейне',
    },
    timeline: {
      locatedFunds: 'Найденные средства',
      fundsTitle: 'Средства, обнаруженные вашими юристами',
      fundsNote: 'Эти записи появляются, только когда юристы делают их видимыми для кабинета.',
      progressLabel: 'Ход юридического дела',
      updatesTitle: 'Обновления по делу',
      updatesNote: 'Ключевые действия, этапы проверки и заметки.',
      publishedBy: 'Публикуется вашими юристами',
      loadingUpdates: 'Загружаем обновления...',
      emptyTitle: 'Обновлений по делу пока нет',
      emptyBody: 'Юристы опубликуют здесь подтверждённые этапы, как только они будут готовы.',
    },
    chat: {
      confidential: 'Конфиденциальная переписка',
      title: 'Защищённые сообщения',
      subtitle: 'Задавайте вопросы, уточняйте по документам и прикрепляйте файлы для вашей команды.',
      emptyTitle: 'Сообщений пока нет',
      emptyBody: 'Напишите юристам, если нужно уточнение, новости по делу или помощь с документом.',
      attachFile: 'Прикрепить файл',
      placeholder: 'Напишите вашим юристам...',
      sendFailed: 'Сообщение не отправлено. Попробуйте ещё раз.',
    },
    documents: {
      centerLabel: 'Документы по делу',
      title: 'Документы и верификация',
      subtitle: 'Загружайте документы для верификации, делитесь файлами по делу и скачивайте материалы от юристов.',
      verifiedBadge: 'Подтверждено: {count}',
      verification: 'Верификация',
      caseFiles: 'Файлы дела',
      files: 'Файлы',
      invoices: 'Счета',
      identityTitle: 'Подтверждение личности для вашего дела',
      identityNote: 'Загружайте чёткие актуальные документы. Юристы отметят каждый как принятый, отклонённый или на проверке.',
      notUploadedYet: 'Ещё не загружено',
      awaitingUpload: 'Ожидает загрузки',
      approvedNote: 'Принято для вашего дела',
      declinedNote: 'Отклонено. Загрузите, пожалуйста, более чёткую версию.',
      pendingNote: 'На проверке у юристов',
      uploadToContinue: 'Загрузите этот документ, чтобы продолжить верификацию',
      caseTeamFilesTitle: 'Файлы для вашей команды',
      caseTeamFilesNote: 'Договоры, переписка, доказательства, выписки и другие материалы.',
      uploadFile: 'Загрузить файл',
      emptyTitle: 'Документов пока нет',
      emptyBody: 'Когда файл будет готов, он появится здесь со статусом проверки.',
      loadingPreview: 'Загружаем предпросмотр...',
      previewUnavailable: 'Предпросмотр недоступен',
      previewUnavailableNote: 'Вы всё равно можете скачать этот документ.',
      previewUnsupported: 'Предпросмотр недоступен для этого типа файла',
      previewUnsupportedNote: 'Скачайте файл, чтобы открыть его на устройстве.',
      closePreview: 'Закрыть предпросмотр',
      uploadFailed: 'Не удалось загрузить файл. Попробуйте ещё раз.',
    },
    verifyTypes: {
      'Passport': 'Паспорт',
      'Driver License': 'Водительские права',
      'ID Front': 'ID — лицевая сторона',
      'ID Back': 'ID — обратная сторона',
      'Utility Bill': 'Счёт за коммунальные услуги',
      'Bank Statement': 'Банковская выписка',
    },
    status: {
      approved: 'Принято',
      declined: 'Отклонено',
      pending: 'На проверке',
      missing: 'Не загружено',
      uploaded: 'Загружено',
      active: 'Активно',
      new: 'Новый',
      inProgress: 'В работе',
      opened: 'Открыто',
      onHold: 'Приостановлено',
      closed: 'Закрыто',
    },
    settings: {
      protection: 'Защита кабинета',
      title: 'Безопасность аккаунта',
      subtitle: 'Сохраняйте доступ к вашему конфиденциальному делу защищённым.',
      currentPassword: 'Текущий пароль',
      currentPlaceholder: 'Введите текущий пароль',
      newPassword: 'Новый пароль',
      newPlaceholder: 'Минимум 6 символов',
      confirmPassword: 'Повторите новый пароль',
      confirmPlaceholder: 'Введите новый пароль ещё раз',
      changed: 'Пароль успешно изменён.',
      saving: 'Сохраняем...',
      update: 'Обновить пароль',
      guidanceTitle: 'Рекомендации по безопасности',
      guidanceBody: 'Используйте уникальный пароль и выходите из кабинета на чужих устройствах. Кабинет отделён от внутренней системы юристов.',
      passwordsDontMatch: 'Новые пароли не совпадают.',
      changeFailed: 'Не удалось изменить пароль.',
    },
    login: {
      title: 'Кабинет клиента',
      subtitle: 'Войдите, чтобы видеть информацию по вашему делу',
      loginLabel: 'Логин (email)',
      passwordLabel: 'Пароль',
      signIn: 'Войти',
      signingIn: 'Входим...',
      invalidCredentials: 'Неверный логин или пароль. Попробуйте ещё раз.',
      autoLoggingIn: 'Открываем кабинет…',
      autoLoginFailed: 'Ссылка недействительна или устарела. Войдите по логину и паролю.',
      invalidLink: 'Недействительная ссылка.',
      gotoLogin: 'Перейти ко входу',
      needHelp: 'Нужна помощь? Свяжитесь с вашим юристом напрямую.',
    },
  },
};

const RU_HINTS = ['ru', 'rus', 'russian', 'русский', 'россия'];

// Best-effort guess from the CRM client.language free-text field.
// Returns 'ru', 'en', or null when there is no confident match.
export const normalizeClientLanguage = (value) => {
  const v = String(value || '').trim().toLowerCase();
  if (!v || v === '-') return null;
  if (RU_HINTS.some(h => v.startsWith(h))) return 'ru';
  if (v.startsWith('en')) return 'en';
  return null;
};
